/**
 * Where MaiTeams' OWN tracking scripts may run.
 *
 * Four loaders read this — Google Analytics (`components/google-analytics.tsx`),
 * Dub (`components/dub-analytics.tsx`), PostHog
 * (`lib/analytics/posthog-client.ts`, at page load AND per event) and the
 * browser Sentry SDK (`instrumentation-client.ts`, through
 * `lib/analytics/sentry-client-scope.ts`, the same two gates) — and they
 * must agree, because the reasons are not about analytics at all:
 *
 *   - A capability URL is a credential. `/q/<token>`, `/demo/s/<token>`,
 *     `/public/preferences/<token>`,
 *     `/data-deletion-status?code=<confirmation code>` and the legacy
 *     `/unsubscribe?email=…&org=…` / `/email-preferences?email=…&org=…`
 *     recipient links … are handed to people who never agreed to anything; a
 *     page-view beacon there is a tracking-consent question we must not answer
 *     on their behalf.
 *   - A credential in an enrollment URL's query is the same class of bearer
 *     capability even though it is not a path segment. Signup, enrollment and
 *     enrollment-RESUME links carrying one must stay invisible to third-party
 *     scripts too: a signed-out invitee is sent from `/enroll` to
 *     `/login?token=<enrollment credential>&email=<address>`
 *     (`app/enroll/page.tsx`), so `/login` is on that list beside `/signup`
 *     and `/enroll`. Only a NON-EMPTY `token`/`invitation` refuses, so an
 *     ordinary login and the `/login?error=invalid_token` bounce still track.
 *   - A customer's published page is the CUSTOMER's traffic. `/p/`, `/lp/domain/`,
 *     `/forms/`, `/embed/` and every white-label host serve someone else's
 *     visitors under someone else's brand; MaiTeams' pixels have no business
 *     there (customer pixels load from `lib/landing-pages/pixels.ts`).
 *
 * Pure. Safe in the browser bundle and in a server component's template.
 *
 * The list is kept as path SEGMENTS and the prefixes are derived, because
 * these are prefixes of families of routes, not links to a route — the
 * route-contract ratchet (`npm run check:routes`) reads every literal that
 * starts with `/` as a link that must resolve, and `/q/` on its own does not.
 */

/** Leading path segments of capability URLs and customer surfaces. */
export const CAPABILITY_PATH_SEGMENTS = [
  'q',
  'v',
  'demo/s',
  'p',
  'lp/domain',
  'forms',
  'embed',
  'public/preferences',
  'public/unsubscribe',
  // app/api/oauth/facebook/data-deletion/route.ts mints the link; lib/meta-signature-verification.ts makes the code its only key.
  'data-deletion-status',
  'share',
  'texting-registration',
  // The legacy recipient links older footers carry. No send path mints them
  // since the owner's 2026-09-23 decision, and app/unsubscribe/page.tsx and
  // app/email-preferences/page.tsx render a "use the link in your email" notice
  // for any query, but a link already in an inbox still puts the recipient's
  // address and the org UUID in the URL of a page that runs the root layout's
  // loaders.
  'unsubscribe',
  'email-preferences',
] as const;

/**
 * Enrollment surfaces whose query may carry a live bearer credential.
 * `/onboard?invitation=<id>` is the franchise invitee's landing: the
 * invitation link arrives with the Supabase session in the URL FRAGMENT
 * (`#access_token=…`, read by `app/onboard/onboard-client.tsx` before it
 * strips it), so the page is a credential surface even though the query
 * carries only the invitation id.
 */
export const CREDENTIAL_QUERY_PATH_SEGMENTS = ['signup', 'enroll', 'login', 'onboard'] as const;

/** Credential parameter names accepted by the enrollment surfaces. */
export const CREDENTIAL_QUERY_PARAMS = ['token', 'invitation'] as const;

const CREDENTIAL_QUERY_PATHS: readonly string[] = CREDENTIAL_QUERY_PATH_SEGMENTS.map(
  (segment) => '/' + segment
);

/** The same list as `/<segment>/` prefixes. */
export const CAPABILITY_PATH_PREFIXES: readonly string[] = CAPABILITY_PATH_SEGMENTS.map(
  (segment) => '/' + segment + '/'
);

/**
 * Decodes each `%XX` escape once, then lowercases. `location.pathname` keeps
 * `/%74exting-registration/<token>` encoded while Next serves the page, so a
 * prefix match on the raw path never sees it. A per-escape replace, not
 * `decodeURIComponent`: a malformed escape cannot throw. The inline scripts in
 * `components/google-analytics.tsx` and `components/new-relic-browser.tsx`
 * repeat this and the scope test keeps them in step.
 */
export function normalizeTrackingPathname(pathname: string): string {
  return pathname
    .replace(/%([0-9a-f]{2})/gi, (_escape, hex: string) => String.fromCharCode(parseInt(hex, 16)))
    .toLowerCase();
}

/**
 * A prefix matches with or without its trailing slash (the bare `/p` 404 too),
 * and regardless of case or percent-encoding: `/Q/<token>` 404s, but the token is still
 * in the URL.
 */
export function isCapabilityPath(pathname: string): boolean {
  const normalizedPathname = normalizeTrackingPathname(pathname);
  return CAPABILITY_PATH_PREFIXES.some(
    (prefix) => normalizedPathname === prefix.slice(0, -1) || normalizedPathname.startsWith(prefix)
  );
}

/** True when an enrollment URL carries a non-empty bearer credential. */
export function isCredentialBearingUrl(
  pathname: string,
  search: string | null | undefined
): boolean {
  // Invitation sign-in links retain the credential in their return URL.
  // Login and middleware can follow multiple hops; judge the whole chain
  // before a tracker can capture the outer URL. Each query embeds a shorter
  // destination, so the worklist ends without recursive call-stack growth.
  const pending = [{ pathname, search }];
  while (pending.length > 0) {
    const target = pending.pop()!;
    if (!target.search) continue;
    const normalizedPathname = normalizeTrackingPathname(target.pathname).replace(/\/+$/, '') || '/';
    if (!CREDENTIAL_QUERY_PATHS.some(
      (path) => normalizedPathname === path || normalizedPathname.startsWith(path + '/')
    )) continue;

    const params = new URLSearchParams(target.search);
    if (CREDENTIAL_QUERY_PARAMS.some((name) => params.getAll(name).some((value) => value.length > 0))) {
      return true;
    }
    for (const value of params.getAll('redirect')) {
      try {
        const callback = new URL(value, 'https://app.example.invalid');
        pending.push({ pathname: callback.pathname, search: callback.search });
      } catch {
        // A malformed callback carries no usable invitation destination.
      }
    }
  }
  return false;
}

export function isAppHost(hostname: string, appRootDomain: string): boolean {
  const h = hostname.toLowerCase();
  const root = appRootDomain.toLowerCase();
  return (
    h === root ||
    h.endsWith(`.${root}`) ||
    h === 'localhost' ||
    h.endsWith('.localhost') ||
    h === '127.0.0.1' ||
    h.endsWith('.vercel.app')
  );
}

/**
 * Lead-form hosts (`<sub>.leads.maiteams.com`) are rewritten server-side to
 * /forms/<sub>, so the BROWSER pathname is "/" and only the hostname can
 * identify them — the prefix list alone is blind to the branded case.
 */
export function isLeadFormHost(hostname: string, leadFormDomain: string): boolean {
  const h = hostname.toLowerCase();
  const d = leadFormDomain.toLowerCase();
  return h === d || h.endsWith(`.${d}`);
}

export interface TrackingScopeInput {
  hostname: string;
  pathname: string;
  search?: string | null;
  appRootDomain: string;
  leadFormDomain: string;
}

/** True when a first-party tracking script may load on this page. */
export function isFirstPartyTrackingSurface(input: TrackingScopeInput): boolean {
  return (
    isAppHost(input.hostname, input.appRootDomain) &&
    !isCapabilityPath(input.pathname) &&
    !isCredentialBearingUrl(input.pathname, input.search) &&
    !isLeadFormHost(input.hostname, input.leadFormDomain)
  );
}
